import React, { useEffect, useState, useRef, useCallback } from "react";
import { getRecentEmails } from "../../../services/email/Email";
import { Email } from "../../../types/widgets/email";
import { useDocMgmtClient } from "../../../context/ApiContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail } from "lucide-react";

type RecentEmailsProps = {
    count?: number;
    autoRefreshIntervalMs?: number | null;
};

const CACHE_TTL_MS = 60_000;
const STORAGE_KEY = 'recentEmails_count';
const cacheStore: Map<number, { ts: number; data: Email[] }> = new Map();
const countOptions = [5, 10, 15];

export default function RecentEmails({
    autoRefreshIntervalMs = null,
}: RecentEmailsProps) {
    const docMgmtClient = useDocMgmtClient();

    const getInitialCount = (): number => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsedCount = parseInt(stored, 10);
                if (countOptions.includes(parsedCount)) return parsedCount;
            }
        } catch (error) {
            console.warn('Failed to read from localStorage:', error);
        }
        return 5;
    };

    const [count, setCount] = useState<number>(getInitialCount);
    const [emails, setEmails] = useState<Email[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<number | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const autoRefreshRef = useRef<number | null>(null);

    const handleCountChange = (newCount: number) => {
        setCount(newCount);
        try {
            localStorage.setItem(STORAGE_KEY, newCount.toString());
        } catch (error) {
            console.warn('Failed to save to localStorage:', error);
        }
    };
    const loadEmails = useCallback(async (opts: { force?: boolean } = {}) => {
        setError(null);
        const key = count;
        const cached = cacheStore.get(key);
        const now = Date.now();
        if (!opts.force && cached && now - cached.ts < CACHE_TTL_MS) {
            setEmails(cached.data.slice(0, count));
            setLastUpdated(cached.ts);
            return;
        }
        setLoading(true);
        try {
            const data = await getRecentEmails(count, docMgmtClient);
            cacheStore.set(key, { ts: Date.now(), data });
            setEmails(data.slice(0, count));
            setLastUpdated(Date.now());
        } catch (err: any) {
            setError(err?.message ?? "Failed to load emails.");
            setEmails([]);
        } finally {
            setLoading(false);
        }
    }, [count, docMgmtClient]);
    useEffect(() => {
        loadEmails();
        const abortController = abortRef.current;
        return () => {
            abortController?.abort();
            if (autoRefreshRef.current) {
                window.clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        };
    }, [loadEmails]);

    useEffect(() => {
        if (autoRefreshIntervalMs && autoRefreshIntervalMs > 0) {
            if (autoRefreshRef.current) window.clearInterval(autoRefreshRef.current);
            autoRefreshRef.current = window.setInterval(() => loadEmails({ force: true }), autoRefreshIntervalMs);
        } else {
            if (autoRefreshRef.current) {
                window.clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        }
        return () => {
            if (autoRefreshRef.current) {
                window.clearInterval(autoRefreshRef.current);
                autoRefreshRef.current = null;
            }
        };
    }, [autoRefreshIntervalMs, loadEmails]);



    function formatDate(iso?: string) {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            const diff = Date.now() - d.getTime();
            if (diff < 24 * 60 * 60 * 1000) {
                return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            }
            return d.toLocaleDateString();
        } catch {
            return iso;
        }
    }

    return (
        <div className="border border-border rounded-md p-2 bg-background max-w-[540px] w-full" role="region" aria-label="Recent Emails">
            <div className="flex gap-2 mb-2 items-center">
                <Button
                    size="sm"
                    onClick={() => window.open('https://mail.teampbs.com', '_blank')}
                >
                    <Mail className="h-4 w-4 mr-1" />
                    Open Mail
                </Button>
                <Select
                    value={count.toString()}
                    onValueChange={(val) => handleCountChange(parseInt(val, 10))}
                >
                    <SelectTrigger className="w-[120px] h-8 text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {countOptions.map((option) => (
                            <SelectItem key={option} value={option.toString()}>
                                {option} emails
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center justify-between mb-2">
                <div />
                <div className="flex gap-2 items-center">
                    <button
                        onClick={() => loadEmails({ force: true })}
                        title="Refresh"
                        aria-label="Refresh emails"
                        className="border-none bg-transparent cursor-pointer p-1.5 text-muted-foreground hover:text-foreground"
                    >
                        ⟳
                    </button>
                    <div className="text-xs text-muted-foreground">
                        {lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </div>
                </div>
            </div>

            {loading && !emails ? (
                <div className="p-3 text-sm text-muted-foreground">Loading…</div>
            ) : error ? (
                <div className="p-3 text-sm text-destructive">{error}</div>
            ) : !emails || emails.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">No recent emails.</div>
            ) : (
                <ul className="list-none p-0 m-0">
                    {emails.map((e, idx) => {
                        const key = `${e.receivedDateTime}-${idx}`;
                        const owaUrl = `https://mail.teampbs.com/owa/#path=/mail/search/rp?query=${encodeURIComponent(e.subject || "")}`;
                        return (
                            <li key={key}>
                                <a
                                    href={owaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex gap-2 px-1.5 py-2 items-start rounded cursor-pointer no-underline text-foreground hover:bg-muted/50"
                                >
                                    <div className="flex-none w-10">
                                        <div className="w-9 h-9 rounded bg-muted text-foreground flex items-center justify-center text-xs">
                                            {e.sender?.charAt(0)?.toUpperCase() ?? "?"}
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold text-foreground">{e.sender}</div>
                                            <div className="ml-auto text-xs text-muted-foreground">{formatDate(e.receivedDateTime)}</div>
                                        </div>
                                        <div className="text-sm text-muted-foreground flex items-center">
                                            {e.subject}
                                            {e.hasAttachments && (
                                                <span title="Has attachments" className="ml-4">📎</span>
                                            )}
                                        </div>
                                    </div>
                                </a>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
